/**
 * Phase 41 Plan 41-11 Task 1 — Surface 8: Admin Templates browser.
 *
 * Read-only 3-tab browser of the Phase 35 code-defined template registries
 * (PaymentType / ProjectType / TaskType). Per Phase 35 design, templates are
 * added via developer pull request, not via UI form-builder — so this page
 * only lists them; it does not create / edit / delete.
 *
 * Hooks come from `useTemplateTypes` (Plan 41-10 Task 1 — also consumed by
 * the trio recommendation banner). The plan code-block referenced
 * `useProjectTemplates` in error; that legacy hook owns the Phase 19
 * template-with-states concept (project_templates table) which is unrelated
 * to the v7.0 template-type trio (PaymentType/ProjectType/TaskType).
 *
 * Defense-in-depth note (checker I-18): the route is gated by `<AdminRoute>`
 * in the App.tsx route tree (plan 41-03), AND the underlying admin endpoints
 * (`/api/admin/templates/*` mounted via projectTemplateAdminRoutes.ts) call
 * `requireAdmin` at the Fastify plugin scope (verified 2026-05-12).
 *
 * MKS-251: the "Migrate existing project" button and the Migration Wizard it
 * opened are gone. Payment / project / task type are set on the project create
 * form and changed in project edit, so this page is a pure registry browser
 * again.
 */
import { useState } from 'react';
import { TopBar } from '../../components/ui/TopBar';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { usePaymentTypes, useProjectTypes, useTaskTypes } from '../../hooks/useTemplateTypes';

type Tab = 'payment' | 'projectType' | 'task';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'payment', label: 'Payment' },
  { id: 'projectType', label: 'Project types' },
  { id: 'task', label: 'Task templates' },
];

function ActivePill({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <Pill bg="#D1FAE5" fg="#047857" dot="#10B981">
      Active
    </Pill>
  ) : (
    <Pill bg="#F4F1EC" fg="#5C5C68" dot="#A0A0A8">
      Inactive
    </Pill>
  );
}

function TrioLine({
  p,
  pt,
  t,
}: {
  p: number | null;
  pt: number | null;
  t: number | null;
}) {
  if (!p && !pt && !t) return <span className="text-ms-text-muted text-[11px]">—</span>;
  return (
    <span className="font-mono text-[11px] text-ms-text-secondary tabular-nums">
      {p ?? '—'} / {pt ?? '—'} / {t ?? '—'}
    </span>
  );
}

export function AdminTemplatesPage() {
  const [tab, setTab] = useState<Tab>('payment');
  const pay = usePaymentTypes();
  const proj = useProjectTypes();
  const tsk = useTaskTypes();

  return (
    <>
      <TopBar
        title="Templates"
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Templates' }]}
      />
      <div className="p-6 max-w-5xl mx-auto">
        <Card className="mb-4">
          <p className="text-[12px] text-ms-text-secondary">
            Templates are defined in code and added via developer pull requests. To request a new
            template, contact your engineering team. To put a project on one of them, open the
            project and edit its payment, project, and task type.
          </p>
        </Card>

        <div className="flex gap-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-medium ${
                tab === t.id
                  ? // 41-14 Task 0b — orange-dark for 5.65:1 contrast on white text
                    'bg-ms-orange-dark text-white'
                  : 'bg-ms-surface border border-ms-border text-ms-text-secondary hover:bg-ms-surface-2'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <Card>
          <table className="min-w-full divide-y divide-ms-border-subtle -mx-4">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-ms-text-muted">
                <th className="px-4 py-2 font-semibold">ID</th>
                <th className="px-4 py-2 font-semibold">Name</th>
                {tab === 'projectType' && <th className="px-4 py-2 font-semibold">Color</th>}
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Recommended trio (P / PT / T)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ms-border-subtle">
              {tab === 'payment' &&
                (pay.data?.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 font-mono tabular-nums text-[11px]">{row.id}</td>
                    <td className="px-4 py-2 text-[12.5px] font-medium text-ms-text-strong">
                      {row.name}
                    </td>
                    <td className="px-4 py-2">
                      <ActivePill isActive={row.isActive} />
                    </td>
                    <td className="px-4 py-2">
                      <TrioLine
                        p={row.id}
                        pt={row.recommendedProjectTypeId}
                        t={row.recommendedTaskTypeId}
                      />
                    </td>
                  </tr>
                ))}
              {tab === 'projectType' &&
                (proj.data?.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 font-mono tabular-nums text-[11px]">{row.id}</td>
                    <td className="px-4 py-2 text-[12.5px] font-medium text-ms-text-strong">
                      {row.name}
                    </td>
                    <td className="px-4 py-2">
                      {row.color ? (
                        <span
                          className="inline-block w-4 h-4 rounded"
                          style={{ background: row.color }}
                          title={row.color}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <ActivePill isActive={row.isActive} />
                    </td>
                    <td className="px-4 py-2">
                      <TrioLine
                        p={row.recommendedPaymentTypeId}
                        pt={row.id}
                        t={row.recommendedTaskTypeId}
                      />
                    </td>
                  </tr>
                ))}
              {tab === 'task' &&
                (tsk.data?.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 font-mono tabular-nums text-[11px]">{row.id}</td>
                    <td className="px-4 py-2 text-[12.5px] font-medium text-ms-text-strong">
                      {row.name}
                    </td>
                    <td className="px-4 py-2">
                      <ActivePill isActive={row.isActive} />
                    </td>
                    <td className="px-4 py-2">
                      <TrioLine
                        p={row.recommendedPaymentTypeId}
                        pt={row.recommendedProjectTypeId}
                        t={row.id}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
